// UnitLandedSteps.js
//
// The steps of the unit view that show what the landing wrote on a landed unit (Spec 4
// sections 3.2, 4 and 8; Spec 2 section 6.4 item 10). They follow Rows after cleaning:
//
//   What this file changed in {table}   unit.diff: one entry per row the unit touched, with
//                                       its identifier, whether the row is new or existing,
//                                       its details and tags (before → after), and its
//                                       amounts per period (+add for an Add metric,
//                                       before → after for an Overwrite one)
//   Values other files overruled        unit.overruled: each value the unit stated that a
//                                       column policy outvoted, captioned with the date its
//                                       batch landed, since the list describes that landing
//   Rows corrected by answers           unit.row_corrections: the changes answers made to
//                                       the unit's rows, grouped by question, each group
//                                       opening its question read-only (QuestionsDialog)
//
// Each record is inline on the unit document while it fits, and otherwise a blob in the
// unit's own folder that the document names as { path }: the diff and the overruled values
// share one blob under diffs/{unit id}/, which holds { diff, overruled } and which both
// fields name; the correction records have their own under row_corrections/{unit id}/,
// which holds the list. The unit view reads each blob once (useLandingBlob) and hands it to
// the steps that show it. A pointer is read off the unit document, which the browser can
// write, so it is followed only inside that folder: anything else could name another
// unit's, or another tenant's, blob.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { fetchGcsBlobByPrefix } from '../../../services/api_utils';
import { PLACEHOLDER_PREFIX } from '../../../services/answers';
import { tableScope } from '../../../utils/blobPaths';
import { rowKeyText } from '../../../utils/decisions';
import { formatPeriodKey, getPeriodType } from '../../../utils/periodUtils';
import { formatDateOnly, formatTimestamp } from '../../../utils/timestamps';
import { formatText } from '../../common/TextFormatterProvider';
import UnitStepFrame, { cellText, isInFolder, targetLabel } from './UnitStepFrame';
import QuestionsDialog from './QuestionsDialog';

// The entries of a long list drawn at once.
const PAGE_ROWS = 50;

// How each kind of correction record reads, in the words of the read-only question.
const ACTION_LABELS = {
  correct_identifier: 'Identifier corrected',
  move_profile: 'Moved to another profile',
  drop_row: 'Left out',
};
// How a move_profile record whose profile stays the same reads: it changed only the row's
// attributes (Spec 3 section 2.2: a profile move carries every attribute, and may change
// attributes alone).
const ATTRIBUTES_CORRECTED_LABEL = 'Attributes corrected';

// ── Reading a spilled record ────────────────────────────────────────────────────────

// A blob the landing stored for a landed unit, read once for its path. `path` is null while
// the unit's records are inline, and the blob is read only inside `folder`, the unit's own
// folder of its family: a path anywhere else is never read. A response for a path no longer
// asked for (the view moved on to another table, or the record was stored anew) is ignored.
//
// Returns { state, value, retry }: state is 'idle' (no path), 'loading', 'loaded' or
// 'failed'; value is the blob's JSON once loaded; retry reads the blob again.
export function useLandingBlob(path, folder) {
  const [read, setRead] = useState({ path: null, state: 'idle', value: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (path === null) return undefined;
    if (!isInFolder(path, folder)) {
      setRead({ path, state: 'failed', value: null });
      return undefined;
    }
    let cancelled = false;
    setRead({ path, state: 'loading', value: null });
    fetchGcsBlobByPrefix(path)
      .then((blob) => blob.text())
      .then((text) => {
        if (!cancelled) setRead({ path, state: 'loaded', value: JSON.parse(text) });
      })
      .catch((error) => {
        console.error('Failed to load a record the landing stored:', error);
        if (!cancelled) setRead({ path, state: 'failed', value: null });
      });
    return () => { cancelled = true; };
  }, [path, folder, attempt]);

  const retry = () => setAttempt((count) => count + 1);
  if (path === null) return { state: 'idle', value: null, retry };
  if (read.path !== path) return { state: 'loading', value: null, retry };
  return { state: read.state, value: read.value, retry };
}

// One of the landing's records as a step shows it: { state, list }, where state is
// 'ready', 'loading' or 'failed'.
//   record   the unit's field: its list, { path } naming the blob that holds it, or, for
//            the diff, null, its value before the unit lands (Spec 2 section 1.2)
//   blob     useLandingBlob's answer for that path
//   field    what the blob holds for this record: 'diff' or 'overruled' from the blob the
//            two share, or null for a blob that is the list itself
function landingList(record, blob, field) {
  if (record === null) return { state: 'ready', list: [] };
  if (Array.isArray(record)) return { state: 'ready', list: record };
  if (blob.state === 'loading') return { state: 'loading', list: [] };
  // Failed, and also idle: a pointer the unit view does not follow (its path is not text).
  if (blob.state !== 'loaded') return { state: 'failed', list: [] };
  const list = field === null ? blob.value : blob.value?.[field];
  if (!Array.isArray(list)) return { state: 'failed', list: [] };
  return { state: 'ready', list };
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────

// What a step shows while its record is not in hand (state 'loading' or 'failed'): a
// spinner while it loads, or the fixed sentence `failure` with Retry, disabled while
// `retryDisabled`.
function LoadNotice({ state, failure, onRetry, retryDisabled }) {
  if (state === 'loading') {
    return <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  }
  return (
    <Alert
      severity="error"
      action={<Button color="inherit" size="small" disabled={retryDisabled} onClick={onRetry}>Retry</Button>}
    >
      {failure}
    </Alert>
  );
}

// A long list drawn PAGE_ROWS entries at a time: `render` draws the entries of the page
// shown, and the pager follows once there is more than one page, disabled while
// `pagerDisabled`.
function Paged({ items, render, pagerDisabled }) {
  const [page, setPage] = useState(0);
  const lastPage = Math.max(Math.ceil(items.length / PAGE_ROWS) - 1, 0);
  const shownPage = Math.min(page, lastPage);
  return (
    <>
      {render(items.slice(shownPage * PAGE_ROWS, (shownPage + 1) * PAGE_ROWS))}
      {items.length > PAGE_ROWS && (
        <TablePagination
          component="div"
          count={items.length}
          page={shownPage}
          rowsPerPage={PAGE_ROWS}
          rowsPerPageOptions={[]}
          disabled={pagerDisabled}
          onPageChange={(event, next) => setPage(next)}
        />
      )}
    </>
  );
}

// ── The steps ───────────────────────────────────────────────────────────────────────

// Each step's fileDeletionSet says that the file's deletion is set: its Retry and its pager are
// then disabled, and so is each "Open the question" of the corrections (Spec 5 section 5).

// Props: number, table, record (unit.diff), blob (useLandingBlob's answer for the blob the
// diff and the overruled values share), fileDeletionSet.
export function FileChangesStep({ number, table, record, blob, fileDeletionSet }) {
  const { state, list: entries } = landingList(record, blob, 'diff');
  const periodType = getPeriodType(table);

  let body;
  if (state !== 'ready') {
    body = (
      <LoadNotice
        state={state}
        failure="The changes could not be loaded."
        onRetry={blob.retry}
        retryDisabled={fileDeletionSet}
      />
    );
  } else if (entries.length === 0) {
    body = <Typography variant="body2" color="text.secondary">None.</Typography>;
  } else {
    body = (
      <Paged
        items={entries}
        pagerDisabled={fileDeletionSet}
        render={(shown) => (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{targetLabel(table.identifier.name)}</TableCell>
                  <TableCell />
                  <TableCell>Details</TableCell>
                  <TableCell>Amounts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((entry) => {
                  // A detail or tag the unit reports: its value after the landing, and the
                  // one before when the landing changed it.
                  const columns = [...Object.entries(entry.details || {}), ...Object.entries(entry.attributes || {})];
                  // Per period, each metric the unit reports: an Add metric's contribution
                  // with its sign, an Overwrite metric's value before and after.
                  const amounts = Object.entries(entry.periods || {}).map(([periodKey, leaves]) => {
                    const changes = Object.entries(leaves).map(([leaf, change]) => {
                      if ('add' in change) {
                        const sign = change.add < 0 ? '' : '+';
                        return `${targetLabel(leaf)} ${sign}${cellText(change.add)}`;
                      }
                      return `${targetLabel(leaf)} ${cellText(change.before)} → ${cellText(change.after)}`;
                    });
                    return `${formatPeriodKey(periodKey, periodType)}: ${changes.join(', ')}`;
                  });
                  return (
                    <TableRow key={entry.identifier}>
                      <TableCell><Typography variant="body2" fontWeight={600}>{entry.identifier}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{entry.created ? 'new' : 'existing'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {columns.map(([column, change], index) => (
                            <React.Fragment key={column}>
                              {index > 0 ? ' · ' : ''}
                              {`${targetLabel(column)}: `}
                              {change.before !== undefined && change.before !== change.after
                                ? `${cellText(change.before)} → `
                                : ''}
                              <b>{cellText(change.after)}</b>
                            </React.Fragment>
                          ))}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption">{amounts.join(' · ')}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      />
    );
  }

  return (
    <UnitStepFrame number={number} title={`What this file changed in ${formatText(table.name)}`} defaultOpen>
      {body}
    </UnitStepFrame>
  );
}

// Props: number, companyId, tableId, batchId (the batch the unit landed in), record
// (unit.overruled), blob (useLandingBlob's answer for the blob the diff and the overruled
// values share), fileDeletionSet.
export function OverruledValuesStep({ number, companyId, tableId, batchId, record, blob, fileDeletionSet }) {
  const { state, list: overruled } = landingList(record, blob, 'overruled');

  // When the batch landed: its completed_at, which its last file's completion writes, and
  // which can come after this unit's own. The batch document is followed until then, and a
  // reading of any batch but the unit's is never shown.
  const [landing, setLanding] = useState({ batchId: null, completedAt: null });
  useEffect(() => {
    if (!batchId) return undefined;
    const batchRef = doc(db, `${tableScope(companyId, tableId)}/batches/${batchId}`);
    return onSnapshot(batchRef, (snapshot) => {
      setLanding({ batchId, completedAt: snapshot.exists() ? snapshot.data().completed_at : null });
    }, (error) => {
      console.error('Error reading when the batch landed:', error);
    });
  }, [companyId, tableId, batchId]);
  const completedAt = landing.batchId === batchId ? landing.completedAt : null;

  let body;
  if (state !== 'ready') {
    body = (
      <LoadNotice
        state={state}
        failure="The overruled values could not be loaded."
        onRetry={blob.retry}
        retryDisabled={fileDeletionSet}
      />
    );
  } else if (overruled.length === 0) {
    body = <Typography variant="body2" color="text.secondary">None.</Typography>;
  } else {
    body = (
      <Paged
        items={overruled}
        pagerDisabled={fileDeletionSet}
        render={(shown) => shown.map((entry) => (
          // One entry per identifier, column, period and asserted value (Spec 3 section 3).
          <Typography
            key={JSON.stringify([entry.identifier, entry.column, entry.period_key, entry.asserted])}
            variant="body2"
          >
            {`${entry.identifier} · ${targetLabel(entry.column)}: ${cellText(entry.asserted)} → `
              + `${cellText(entry.belief)} · ${entry.reason}`}
          </Typography>
        ))}
      />
    );
  }

  return (
    <UnitStepFrame number={number} title="Values other files overruled">
      {completedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {`At landing on ${formatDateOnly(completedAt)}`}
        </Typography>
      )}
      {body}
    </UnitStepFrame>
  );
}

// Props: number, companyId, tableId, table, file, currentUid (who is looking, for whose
// decisions they are), record (unit.row_corrections), blob (useLandingBlob's answer for its
// blob), fileDeletionSet.
export function RowCorrectionsStep({
  number, companyId, tableId, table, file, currentUid, record, blob, fileDeletionSet,
}) {
  const [openQuestion, setOpenQuestion] = useState(null);   // { batchId, questionId, corrections }
  const { state, list: corrections } = landingList(record, blob, null);
  const identifierLabel = targetLabel(table.identifier.name);

  // The statements grouped by question, in the order they were recorded. A question's id is
  // its subject's, which a later batch can ask about again, so a question is its batch and id.
  const groupsByQuestion = new Map();
  corrections.forEach((correction) => {
    const questionKey = JSON.stringify([correction.batch_id, correction.question_id]);
    if (!groupsByQuestion.has(questionKey)) {
      groupsByQuestion.set(questionKey, {
        batchId: correction.batch_id, questionId: correction.question_id, corrections: [],
      });
    }
    groupsByQuestion.get(questionKey).corrections.push(correction);
  });
  const groups = [...groupsByQuestion.values()];

  let body;
  if (state !== 'ready') {
    body = (
      <LoadNotice
        state={state}
        failure="The corrections could not be loaded."
        onRetry={blob.retry}
        retryDisabled={fileDeletionSet}
      />
    );
  } else if (groups.length === 0) {
    body = <Typography variant="body2" color="text.secondary">None.</Typography>;
  } else {
    body = (
      <Paged
        items={groups}
        pagerDisabled={fileDeletionSet}
        render={(shown) => shown.map((group) => {
          // The group's statements by how each reads, one line each, in the order they were
          // recorded: a move_profile record that keeps its profile is not a move.
          const statementsByLabel = new Map();
          group.corrections.forEach((correction) => {
            let label = ACTION_LABELS[correction.action];
            if (correction.action === 'move_profile' && correction.from_profile === correction.to_profile) {
              label = ATTRIBUTES_CORRECTED_LABEL;
            }
            if (!statementsByLabel.has(label)) statementsByLabel.set(label, []);
            statementsByLabel.get(label).push(correction);
          });
          return (
            <Box
              key={JSON.stringify([group.batchId, group.questionId])}
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              {[...statementsByLabel].map(([label, statements]) => {
                // Each statement's row, by its key in the unit: a document row by its page
                // and record, an unpivoted row with its period label.
                const rows = statements.map((statement) => {
                  let key = { row: statement.row };
                  if (statement.page !== null) key = { page: statement.page, record: statement.record };
                  else if (statement.period_label) key = { row: statement.row, period: statement.period_label };
                  return rowKeyText(key);
                });
                // Who decided and when: the decision of the answer's slot, which the
                // statements of one action share.
                const decisions = new Set(statements.map((statement) => (
                  `by ${statement.decided_by === currentUid ? 'you' : 'another user'}, `
                    + `${formatTimestamp(statement.decided_at)}`
                )));
                let line = label;
                if (label === ACTION_LABELS.correct_identifier) {
                  // A row filed with no identifier carried a placeholder in its place.
                  const changes = new Set(statements.map((statement) => {
                    const before = String(statement.identifier).startsWith(PLACEHOLDER_PREFIX)
                      ? `no ${identifierLabel}`
                      : statement.identifier;
                    return `${before} → ${statement.to}`;
                  }));
                  line += `: ${[...changes].join(', ')}`;
                }
                line += ` · ${rows.join(', ')} · ${[...decisions].join('; ')}`;
                return <Typography key={label} variant="body2">{line}</Typography>;
              })}
              {/* A link drawn as a button has no disabled look of its own: it is given one. */}
              <Link
                component="button"
                variant="body2"
                underline="hover"
                disabled={fileDeletionSet}
                onClick={() => setOpenQuestion(group)}
                sx={{ '&:disabled': { color: 'text.disabled', cursor: 'default', textDecoration: 'none' } }}
              >
                Open the question
              </Link>
            </Box>
          );
        })}
      />
    );
  }

  return (
    <>
      <UnitStepFrame number={number} title="Rows corrected by answers">
        {body}
      </UnitStepFrame>
      {openQuestion && (
        <QuestionsDialog
          open
          companyId={companyId}
          tableId={tableId}
          table={table}
          fileNames={{ [file.id]: file.name }}
          readOnly={openQuestion}
          onClose={() => setOpenQuestion(null)}
        />
      )}
    </>
  );
}
